/**
 * User-created playlists — localStorage with optional server sync via StudioPlaylistsContext.
 */

const STORAGE_KEY = 'studio_user_playlists';

function storageKey(userId) {
  return userId ? `${STORAGE_KEY}:${userId}` : `${STORAGE_KEY}:guest`;
}

function readRaw(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeRaw(key, playlists) {
  localStorage.setItem(key, JSON.stringify(playlists));
}

export function loadUserPlaylists(userId) {
  return readRaw(storageKey(userId));
}

export function saveUserPlaylists(userId, playlists) {
  writeRaw(storageKey(userId), playlists);
}

export function createPlaylistInList(playlists, { name, description = '', regionId = '' } = {}) {
  const playlist = {
    id: `pl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: String(name || 'Untitled playlist').trim() || 'Untitled playlist',
    description: String(description || '').trim(),
    regionId: regionId || '',
    tracks: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  // Tuple: [new playlist, next playlists ARRAY]. (Spreading the list into the
  // tuple here made `next` undefined when the user had no playlists yet.)
  return [playlist, [playlist, ...playlists]];
}

export function createPlaylist(userId, opts) {
  const playlists = loadUserPlaylists(userId);
  const [playlist, next] = createPlaylistInList(playlists, opts);
  saveUserPlaylists(userId, next);
  return playlist;
}

export function updatePlaylistInList(playlists, playlistId, patch = {}) {
  const idx = playlists.findIndex((p) => p.id === playlistId);
  if (idx < 0) return playlists;
  const current = playlists[idx];
  const next = { ...current, ...patch, updatedAt: new Date().toISOString() };
  // Never allow a blank name — fall back to the previous one.
  if (typeof patch.name === 'string') {
    next.name = patch.name.trim() || current.name || 'Untitled playlist';
  }
  if (typeof patch.description === 'string') {
    next.description = patch.description.trim();
  }
  return playlists.map((p, i) => (i === idx ? next : p));
}

export function updatePlaylist(userId, playlistId, patch) {
  const playlists = updatePlaylistInList(loadUserPlaylists(userId), playlistId, patch);
  saveUserPlaylists(userId, playlists);
  return playlists.find((p) => p.id === playlistId) || null;
}

export function duplicatePlaylistInList(playlists, playlistId) {
  const idx = playlists.findIndex((p) => p.id === playlistId);
  if (idx < 0) return playlists;
  const source = playlists[idx];
  const now = new Date().toISOString();
  const copy = {
    ...source,
    id: `pl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: `${source.name} copy`,
    tracks: (source.tracks || []).map((t) => ({
      ...t,
      id: `tr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    })),
    createdAt: now,
    updatedAt: now,
  };
  return [...playlists.slice(0, idx + 1), copy, ...playlists.slice(idx + 1)];
}

export function deletePlaylistFromList(playlists, playlistId) {
  return playlists.filter((p) => p.id !== playlistId);
}

export function deletePlaylist(userId, playlistId) {
  const playlists = deletePlaylistFromList(loadUserPlaylists(userId), playlistId);
  saveUserPlaylists(userId, playlists);
  return playlists;
}

function trackDedupeKey(track) {
  if (track?.kind && track?.sourceId) return `${track.kind}:${track.sourceId}`;
  return `${track?.title || ''}|${track?.artist || ''}|${track?.url || ''}`.toLowerCase();
}

export function addTrackToPlaylistList(playlists, playlistId, track) {
  const idx = playlists.findIndex((p) => p.id === playlistId);
  if (idx < 0) return playlists;
  const entry = {
    id: `tr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: String(track.title || '').trim() || 'Untitled',
    artist: String(track.artist || '').trim(),
    url: String(track.url || '').trim(),
    genre: String(track.genre || '').trim(),
    duration: String(track.duration || '').trim(),
    kind: String(track.kind || '').trim(),
    sourceId: String(track.sourceId || '').trim(),
    payload: track.payload && typeof track.payload === 'object' ? track.payload : {},
  };
  const key = trackDedupeKey(entry);
  const exists = playlists[idx].tracks.some((t) => trackDedupeKey(t) === key);
  if (exists) return playlists;
  const next = playlists.map((p, i) =>
    i === idx
      ? {
          ...p,
          tracks: [...p.tracks, entry],
          updatedAt: new Date().toISOString(),
        }
      : p,
  );
  return next;
}

export function addTrackToPlaylist(userId, playlistId, track) {
  const playlists = loadUserPlaylists(userId);
  const next = addTrackToPlaylistList(playlists, playlistId, track);
  saveUserPlaylists(userId, next);
  const pl = next.find((p) => p.id === playlistId);
  return pl?.tracks[pl.tracks.length - 1] || null;
}

export function removeTrackFromPlaylistList(playlists, playlistId, trackId) {
  const idx = playlists.findIndex((p) => p.id === playlistId);
  if (idx < 0) return playlists;
  return playlists.map((p, i) =>
    i === idx
      ? {
          ...p,
          tracks: p.tracks.filter((t) => t.id !== trackId),
          updatedAt: new Date().toISOString(),
        }
      : p,
  );
}

export function removeTrackFromPlaylist(userId, playlistId, trackId) {
  const playlists = loadUserPlaylists(userId);
  const next = removeTrackFromPlaylistList(playlists, playlistId, trackId);
  saveUserPlaylists(userId, next);
}

/**
 * Move a track to an absolute index inside its playlist (drag & drop).
 * Out-of-range / no-op moves return the ORIGINAL array so callers can skip a re-render.
 */
export function reorderTracksInPlaylistList(playlists, playlistId, fromIndex, toIndex) {
  const idx = playlists.findIndex((p) => p.id === playlistId);
  if (idx < 0) return playlists;
  const tracks = playlists[idx].tracks || [];
  const from = Number(fromIndex);
  const to = Number(toIndex);
  if (!Number.isInteger(from) || !Number.isInteger(to)) return playlists;
  if (from < 0 || from >= tracks.length) return playlists;
  if (to < 0 || to >= tracks.length) return playlists;
  if (from === to) return playlists;

  const nextTracks = [...tracks];
  const [moved] = nextTracks.splice(from, 1);
  nextTracks.splice(to, 0, moved);

  return playlists.map((p, i) =>
    i === idx ? { ...p, tracks: nextTracks, updatedAt: new Date().toISOString() } : p,
  );
}

/** Move a track by a relative offset (-1 = up, +1 = down). */
export function moveTrackInPlaylistList(playlists, playlistId, trackId, delta) {
  const idx = playlists.findIndex((p) => p.id === playlistId);
  if (idx < 0) return playlists;
  const tracks = playlists[idx].tracks || [];
  const from = tracks.findIndex((t) => t.id === trackId);
  if (from < 0) return playlists;
  return reorderTracksInPlaylistList(playlists, playlistId, from, from + Number(delta || 0));
}
